import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomContainer,
  LoadingContainer,
} from "../../../../components/Containers";
import {
  getEcommerceCategories,
  getEcommerceSubCategories,
  getEcommerceSubSubCategories,
} from "../../../../services/supplier/categoriesApiFn";
import {
  createSupplierProduct,
  getSupplierSingleProductById,
  updateProductVariantStatus,
  updateSupplierProduct,
} from "../../../../services/supplier/productApiFn";
import {
  calculateProductFinalPrice,
  decodeHtml,
  overAllProductQuantity,
  validateVariants,
} from "../../../../utils";
import { fetchSettings } from "../../../../lib/slices/settingSlice";
import { handleOpenSnackBar } from "../../../../utils";
import AppLoader from "../../../../components/AppLoader";
import GlobalButton from "../../../../components/CustomButton";
import Label from "../../../../components/Label";
import { UploaderProductImages } from "../../../../components/Uploader";
import BasicInformation from "./BasicInformation";
import Pricing from "./Pricing";
import { DEFAULT_VAT_PERCENTAGE } from "../../../../constants/Variables";
import { ProductVariantManager } from "./Variants";
import DescriptionAndSpecification from "./Description&Specification";
import { addEditProductValidation } from "../../../../utils/Validation";
import { setProductCurrentPage } from "../../../../lib/slices/paginationSlice";
import { PricingPreviewDialog } from "./PricingPreviewDialog";
import { getBrands } from "../../../../services/supplier/brandApiFn";

const initialLoadingState = {
  image: false,
  allCategories: false,
  allSubCategories: false,
  allSubSubCategories: false,
  allEcommerceTags: false,
  allBrands: false,
  addProduct: false,
  detail: false,
  updateVariantStatus: false,
};

const initialInputValues = {
  type: "Product",
  productType: "normal",
  name_en: "",
  name_ro: "",
  category: "",
  subCategory: "",
  subSubCategory: "",
  description_en: "",
  description_ro: "",
  images: [],
  brand: "",
  quantity: 0,
  price: 0,
  profitMargin: 0,
  discount: 0,
  sku: "",
  specification_en: "",
  specification_ro: "",
  solutionType: [],
};

export const AddEditProduct = ({ handleChangeTab }) => {
  // Error Ref
  const errorRef = useRef(null);
  // Dispatch Redux
  const dispatch = useDispatch();
  // Translation Hook
  const { t } = useTranslation();
  // All Categories
  const [allCategories, setAllCategories] = useState([]);
  // All Sub Categories
  const [allSubCategories, setAllSubCategories] = useState([]);
  // All Sub Sub Categories
  const [allSubSubCategories, setAllSubSubCategories] = useState([]);
  // All Brands
  const [allBrands, setAllBrands] = useState([]);
  // All Ecommerce Tags
  const [allEcommerceTags, setAllEcommerceTags] = useState([]);
  // Loading State
  const [isloading, setIsLoading] = useState(initialLoadingState);
  // Preview Open
  const [previewOpen, setPreviewOpen] = useState(false);
  // Input Error
  const [inputError, setInputError] = useState({});
  // Input Values
  const [inputValues, setInputValues] = useState(initialInputValues);
  // Attributes Parts
  const [attributes, setAttributes] = useState([]);
  // Variants Parts
  const [variants, setVariants] = useState([]);
  // Get User Data from Redux
  const userData = useSelector((state) => state.userData.user);
  // Get Active tab from Redux
  const activeTab = useSelector((state) => state.activeTab.tabs);
  // Get Product Id from Redux (Only for Edit Product)
  const productId = useSelector((state) => state.productIdReducer?.productId);
  // Get Product Page Type from Redux
  const productPageType = useSelector(
    (state) => state.commonReducer?.productPageType,
  );
  // Get Setting from the Redux
  const { settings, loading: settingsLoading } = useSelector(
    (state) => state.settingsReducer,
  );

  // Get All Ecommerce Categories
  const fetchchedAllCategories = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, allCategories: true }));
      const response = await getEcommerceCategories();
      if (response?.data?.status === "success") {
        setAllCategories(response?.data?.data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, allCategories: false }));
    }
  };

  const fetchchedAllSubCategories = async (value) => {
    try {
      setIsLoading((prev) => ({ ...prev, allSubCategories: true }));
      const response = await getEcommerceSubCategories({ categoryId: value });
      if (response?.data?.status === "success") {
        setAllSubCategories(response?.data?.data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, allSubCategories: false }));
    }
  };

  const fetchchedAllSubSubCategories = async (value1, value2) => {
    try {
      const response = await getEcommerceSubSubCategories({
        categoryId: value1,
        subCategoryId: value2,
      });
      if (response?.data?.status === "success") {
        setAllSubSubCategories(response?.data?.data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, allSubSubCategories: false }));
    }
  };

  const fetchchAllBrands = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, brands: true }));
      const response = await getBrands({ page: 1, limit: 500 });
      if (response?.data?.status === "success") {
        setAllBrands(response?.data?.data?.data);
      } else {
        handleOpenSnackBar(response?.data?.message, "error");
      }
    } catch (error) {
      handleOpenSnackBar(error?.message, "error");
    } finally {
      setIsLoading((prev) => ({ ...prev, brands: false }));
    }
  };

  const fetchchedProductDetail = async (id) => {
    try {
      setIsLoading((prev) => ({ ...prev, detail: true }));
      const response = await getSupplierSingleProductById(id);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;

        if (data?.categoryId) {
          fetchchedAllSubCategories(data?.categoryId?._id);
        }
        if (data?.categoryId && data?.subCategoryId?.length > 0) {
          fetchchedAllSubSubCategories(
            data?.categoryId?._id,
            data?.subCategoryId[0]?._id,
          );
        }

        setInputValues((prev) => ({
          ...prev,
          images: data.images,
          name_en: data.name.en,
          name_ro: data.name.ro,
          type: data.type,
          category: data.categoryId?._id,
          subCategory: data.subCategoryId[0]?._id || "",
          subSubCategory: data.subSubCategoryId[0]?._id || "",
          brand: data.brand,
          quantity: data.quantity,
          sku: data.sku || "",
          description_en: data.description.en,
          description_ro: data.description.ro,
          price: data.price,
          discount: data.discount,
          profitMargin: data.profitMargin || 0,
          specification_en: decodeHtml(data.specification.en),
          specification_ro: decodeHtml(data.specification.ro),
          productType: data.isVariant ? "variant" : "normal",
          solutionType: data.solutionType || [],
        }));
        setAttributes(data.variantAttributes || []);
        setVariants(data.variants || []);
      } else {
        handleOpenSnackBar(response?.data?.message, "error");
      }
    } catch (error) {
      handleOpenSnackBar(error?.message, "error");
    } finally {
      setIsLoading((prev) => ({ ...prev, detail: false }));
    }
  };

  useEffect(() => {
    fetchchedAllCategories();
    fetchchAllBrands();
    dispatch(fetchSettings());
  }, []);

  //   useEffect(() => {
  //     if (inputValues.solutionType?.length > 0) {
  //       fetchchedAllEcommerceTags(inputValues.solutionType);
  //     }
  //   }, [inputValues.solutionType]);

  useEffect(() => {
    if (productPageType === "edit") {
      fetchchedProductDetail(productId);
    }
  }, [productPageType, productId]);

  const scrollToError = () => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      // Add small delay before focus to ensure smooth scroll completes
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.focus();
        }
      }, 300);
    }
  };

  // Check if the product is a variant
  const isVariant = useMemo(() => {
    return attributes.length > 0 && variants.length > 0;
  }, [attributes, variants]);

  // console.log(attributes, "attributes");
  // console.log(variants, "variants");
  // console.log(isVariant, "isVariant");

  const handleChangeProductType = (event) => {
    const value = event.target.value;
    setInputValues((prev) => ({
      ...prev,
      productType: value,
      price: 0,
      profitMargin: 0,
      discount: 0,
      quantity: 0,
      sku: "",
    }));
    setInputError((prev) => ({
      ...prev,
      price: "",
      profitMargin: "",
      discount: "",
      quantity: "",
      sku: "",
    }));
    setAttributes([]);
    setVariants([]);
  };

  const handleAddProduct = async () => {
    const { images, price, discount, profitMargin } = inputValues;

    // Reset errorRef before validation
    errorRef.current = null;

    // Validate the final input values
    const validate = addEditProductValidation(
      inputValues,
      setInputError,
      errorRef,
    );

    if (!validate) {
      scrollToError();
      return handleOpenSnackBar("error", t("validation.resolveTheIssues"));
    }

    // attributes and variants validation

    if (inputValues?.productType === "variant") {
      if (attributes?.length === 0) {
        handleOpenSnackBar(
          "error",
          t("validation.pleaseAddAtLeastOneAttribute"),
        );
        return;
      }

      if (variants?.length === 0) {
        handleOpenSnackBar("error", t("validation.pleaseAddAtLeastOneVariant"));
        return;
      }

      const isValidVariants = validateVariants(variants);
      if (isValidVariants?.length > 0) {
        const message = isValidVariants.map((item) => t(item)).join(", ");
        handleOpenSnackBar("error", message);
        return;
      }
    }

    // Calculate the final Product amount
    const finalAmount = calculateProductFinalPrice(
      price,
      discount,
      profitMargin,
    );

    const finalQuantity =
      inputValues.productType === "variant"
        ? overAllProductQuantity(variants)
        : inputValues.quantity;

    const payload = {
      // Images
      images: inputValues.images,
      coverImage: images[0],

      // Basic Information
      name: {
        en: inputValues.name_en,
        ro: inputValues.name_ro,
      },
      categoryId: inputValues.category,
      subCategoryId: [inputValues.subCategory],
      subSubCategoryId: [inputValues.subSubCategory],
      brand: inputValues.brand,
      type: inputValues.type,
      createdBy: userData?._id,
      solutionType: inputValues.solutionType,
      // tags: inputValues.tags,

      // Pricing
      ...(productPageType === "add" && {
        price: price,
        discount: discount,
        finalPrice: finalAmount || 0,
        profitMargin: profitMargin || 0,
        // Quantity
        quantity: finalQuantity,
        initialQuantity: finalQuantity,
      }),

      ...(productPageType === "add" && !isVariant && { sku: inputValues.sku }),

      isVariant,
      // Variant
      ...(productPageType === "add" && {
        ...(isVariant && { variantAttributes: attributes }),
        ...(isVariant && {
          variants: variants.map((variant) => ({
            ...variant,
            finalPrice:
              calculateProductFinalPrice(
                variant.price,
                variant.discount,
                variant.profitMargin,
              ) || 0,
          })),
        }),
      }),
      // Tags
      // tags: inputValues.tags,

      // Description and Specification
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      specification: {
        en: inputValues.specification_en,
        ro: inputValues.specification_ro,
      },
    };

    try {
      setIsLoading((prev) => ({ ...prev, addProduct: true }));

      let response;
      if (productPageType === "edit") {
        response = await updateSupplierProduct(productId, payload);
      } else {
        response = await createSupplierProduct(payload);
      }
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response?.data.message);
        handleChangeTab("productList");
        if (productPageType === "add") {
          dispatch(setProductCurrentPage(1));
        }
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, addProduct: false }));
    }
  };

  const handleUpdateVariantStatus = async (variantId, event) => {
    if (!productId || !variantId) {
      handleOpenSnackBar("error", t("validation.missingProductIdOrVariantId"));
      return;
    }

    try {
      setIsLoading((prev) => ({ ...prev, updateVariantStatus: true }));
      const response = await updateProductVariantStatus(productId, variantId, {
        status: event.target.checked,
      });
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, updateVariantStatus: false }));
    }
  };

  const showNormalProductPricingPreview = useMemo(() => {
    return (
      inputValues.price > 0 &&
      inputValues.profitMargin >= 0 &&
      inputValues.discount >= 0 &&
      !isVariant
    );
  }, [
    inputValues.price,
    inputValues.profitMargin,
    inputValues.discount,
    isVariant,
  ]);

  if (
    isloading.allCategories ||
    settingsLoading ||
    isloading.detail ||
    isloading.allBrands
  ) {
    return <LoadingContainer />;
  }

  return (
    <React.Fragment>
      <AppLoader isVisible={isloading.addProduct} />
      <CustomContainer
        primaryHeading={
          productPageType === "edit"
            ? t("supplierProductTab.editProduct")
            : t("supplierProductTab.addNewProduct")
        }
        secondaryHeading={t("supplierProductTab.provideRequiredDetails")}
        onBackButton={() => handleChangeTab("productList")}
      >
        <Grid container spacing={2}>
          <Grid item md={12}>
            <Typography fontSize={20} lineHeight={"normal"} fontWeight={600}>
              {t("supplierProductTab.productMedia")}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={400}
              color={"text.secondary"}
              lineHeight={"normal"}
              mt={1}
            >
              {t("supplierProductTab.productMediaDescription1")}{" "}
              {t("supplierProductTab.productMediaDescription2")}
            </Typography>
          </Grid>

          <Grid item xs={12} mt={2}>
            <UploaderProductImages
              inputValues={inputValues}
              setInputValues={setInputValues}
              inputError={inputError}
              setInputError={setInputError}
            />
          </Grid>
          {/* Basic Information */}
          <BasicInformation
            inputValues={inputValues}
            setInputValues={setInputValues}
            allCategories={allCategories}
            allSubCategories={allSubCategories}
            allSubSubCategories={allSubSubCategories}
            inputError={inputError}
            setInputError={setInputError}
            fetchchedAllSubCategories={fetchchedAllSubCategories}
            fetchchedAllSubSubCategories={fetchchedAllSubSubCategories}
            allBrands={allBrands}
          />
          {/* Product Type */}
          <Grid item xs={12}>
            <Typography fontSize={20} fontWeight={600}>
              {t("supplierProductTab.selectYourProductType")}
            </Typography>
            <Typography fontSize={14} color={"text.secondary"}>
              {t("supplierProductTab.selectYourProductTypeDescription")}
            </Typography>
            <FormControl>
              <RadioGroup
                row
                value={inputValues.productType}
                onChange={handleChangeProductType}
              >
                <FormControlLabel
                  value={"normal"}
                  control={<Radio />}
                  label={t("supplierProductTab.normalProduct")}
                  disabled={productPageType === "edit"}
                />
                <FormControlLabel
                  value={"variant"}
                  control={<Radio />}
                  label={t("supplierProductTab.variantProduct")}
                  disabled={productPageType === "edit"}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {inputValues.productType === "normal" && (
            <Pricing
              productPageType={productPageType}
              errorRef={errorRef}
              inputValues={inputValues}
              setInputValues={setInputValues}
              inputError={inputError}
              setInputError={setInputError}
              vat={settings?.tax || DEFAULT_VAT_PERCENTAGE}
            />
          )}
        </Grid>

        {inputValues.productType === "variant" && (
          <ProductVariantManager
            productPageType={productPageType}
            attributes={attributes}
            setAttributes={setAttributes}
            variants={variants}
            setVariants={setVariants}
            vat={settings?.tax || DEFAULT_VAT_PERCENTAGE}
            onUpdateVariantStatus={handleUpdateVariantStatus}
          />
        )}

        <DescriptionAndSpecification
          inputValues={inputValues}
          setInputValues={setInputValues}
          inputError={inputError}
          setInputError={setInputError}
        />

        <Stack
          mt={10}
          mb={3}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-end"}
          gap={2}
        >
          {showNormalProductPricingPreview && (
            <Stack minWidth={120} height={40}>
              <GlobalButton
                styleProps={{
                  backgroundColor: "#FFF",
                  color: "primary.main",
                  border: "1px solid",
                  borderColor: "primary.main",
                }}
                btnText={t("preview")}
                hoverProperties={{
                  color: "primary.main",
                  backgroundColor: "#FFF",
                }}
                handleClickBtn={() => setPreviewOpen(true)}
              />
            </Stack>
          )}
          <Stack minWidth={120} height={40} mr={2}>
            <GlobalButton
              styleProps={{
                backgroundColor: "#FFF",
                color: "primary.main",
              }}
              btnText={"discard"}
              hoverProperties={{
                color: "primary.main",
                backgroundColor: "#FFF",
              }}
              handleClickBtn={() => handleChangeTab("productList")}
            />
          </Stack>
          <Stack minWidth={120} height={40}>
            <GlobalButton btnText={"save"} handleClickBtn={handleAddProduct} />
          </Stack>
        </Stack>
      </CustomContainer>
      <PricingPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        inputValues={inputValues}
        settings={settings}
      />
    </React.Fragment>
  );
};
